import React from "react";
import { Box, Skeleton, Card, CardContent } from "@mui/material";
import { useGenerationStatus } from "../hooks/useGenerationStatus";
import GeneratingView from "./GeneratingView";
import ReportsView from "./ReportsView";

interface StockAnalysisReportDisplayProps {
  ticker: string;
  companyName: string;
}

/**
 * Loading skeleton for the stock analysis report section
 */
const StockAnalysisReportSkeleton: React.FC = () => {
  return (
    <Box sx={{ px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }}>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Skeleton variant="text" width={200} height={30} sx={{ mb: 2 }} />
          {Array.from({ length: 12 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="text"
              width={index % 3 === 0 ? "80%" : "100%"}
              height={20}
              sx={{ mb: 1 }}
            />
          ))}
        </CardContent>
      </Card>
    </Box>
  );
};

/**
 * StockAnalysisReportDisplay - Container component for AI Analysis section.
 *
 * This is the top-level component that:
 * 1. Checks generation status via react-query (polls every 15s)
 * 2. Routes to GeneratingView when a generation is in progress
 * 3. Routes to ReportsView when no generation is active
 *
 * By keeping the isGenerating check at this level, we avoid issues where
 * navigation away and back would lose the generating state.
 */
const StockAnalysisReportDisplay: React.FC<StockAnalysisReportDisplayProps> = ({
  ticker,
  companyName,
}) => {
  // Poll generation status every 15 seconds
  // This is the single source of truth for whether generation is in progress
  const {
    data: generationStatus,
    isLoading: isLoadingStatus,
  } = useGenerationStatus(ticker, {
    pollingInterval: 15000, // Poll every 15 seconds
  });

  // Show skeleton while initially loading status
  if (isLoadingStatus) {
    return <StockAnalysisReportSkeleton />;
  }

  // Route based on generation status
  const isGenerating = generationStatus?.isGenerating === true;

  // If generating, show the generating view
  if (isGenerating) {
    return <GeneratingView ticker={ticker} />;
  }

  // Otherwise, show the reports view (handles no reports, report selection, etc.)
  return <ReportsView ticker={ticker} companyName={companyName} />;
};

export default StockAnalysisReportDisplay;
